// Talks to Zebra Browser Print, the little service that runs on the user's own
// machine. The Rails server can't reach a USB printer — this is the only path.
//
// We speak to the service's localhost HTTP endpoints directly rather than
// through Zebra's official BrowserPrint.js, which isn't on npm (it needs a
// Zebra login) and so can't be relied on to be there. See public/zebra/README.

const STORAGE_KEY = 'zebra.preferred_printer_uid'
const HTTP_ORIGINS = ['https://127.0.0.1:9101', 'http://127.0.0.1:9100']
const REQUEST_TIMEOUT = 8000

export class BrowserPrintError extends Error {
  constructor(code, message) {
    super(message)
    this.name = 'BrowserPrintError'
    this.code = code
  }
}

// The browser can't tell "never installed" from "installed but not running" —
// both are just a refused localhost connection. One message covers both.
const NOT_RUNNING = () =>
  new BrowserPrintError(
    'not_installed_or_running',
    "Zebra Browser Print isn't responding. Make sure it's installed and running on this computer, then try again."
  )

// --- transport: the service's localhost HTTP API ---------------------------

const httpTransport = (origin) => ({
  name: 'http',
  origin,
  async listPrinters() {
    const body = await request(origin, '/available')
    const parsed = JSON.parse(body)
    return (parsed.printer || []).map(normalizePrinter)
  },
  async defaultPrinter() {
    const body = await request(origin, '/default')
    const printer = parseDefault(body)
    return printer ? normalizePrinter(printer) : null
  },
  async send(printer, data) {
    try {
      await request(origin, '/write', {
        method: 'POST',
        body: JSON.stringify({ device: printer.__raw, data })
      })
    } catch (e) {
      if (e instanceof BrowserPrintError) throw e
      throw new BrowserPrintError('write_failed', writeFailureMessage(e, printer))
    }
  }
})

async function request(origin, path, options = {}) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)
  try {
    const response = await fetch(`${origin}${path}`, {
      ...options,
      signal: controller.signal,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' }
    })
    if (!response.ok) {
      throw new BrowserPrintError('write_failed', `Browser Print rejected the request (HTTP ${response.status}).`)
    }
    return await response.text()
  } catch (e) {
    if (e instanceof BrowserPrintError) throw e
    throw NOT_RUNNING()
  } finally {
    clearTimeout(timer)
  }
}

// /default answers with tab-delimited fields rather than JSON
function parseDefault(body) {
  if (!body || !body.trim()) return null
  const [name, deviceType, connection, uid, provider, manufacturer] = body.split('\t')
  if (!uid && !name) return null
  return { name, deviceType, connection, uid: uid || name, provider, manufacturer }
}

function normalizePrinter(device) {
  return {
    uid: device.uid || device.name,
    name: device.name || device.uid,
    connection: device.connection,
    deviceType: device.deviceType,
    provider: device.provider,
    manufacturer: device.manufacturer,
    version: device.version,
    __raw: device
  }
}

function writeFailureMessage(err, printer) {
  const label = (printer && printer.name) || 'the printer'
  const detail = typeof err === 'string' ? err : err && err.message
  if (detail && /not connected|unable to establish|no such device/i.test(detail)) {
    return `${label} didn't answer. Check that it's powered on and its USB cable is connected.`
  }
  return `${label} rejected the print job${detail ? `: ${detail}` : '.'}`
}

// --- transport selection ---------------------------------------------------

let cachedTransport = null

// the service listens on https first, http second; whichever answers wins
async function transport() {
  if (cachedTransport) return cachedTransport

  for (const origin of HTTP_ORIGINS) {
    try {
      await request(origin, '/available')
      cachedTransport = httpTransport(origin)
      return cachedTransport
    } catch (e) {
      // try the next origin; if none answer we report not-running below
    }
  }
  throw NOT_RUNNING()
}

// --- public API ------------------------------------------------------------

const ZebraBrowserPrint = {
  BrowserPrintError,

  async listPrinters() {
    const t = await transport()
    const printers = await t.listPrinters()
    if (!printers.length) {
      throw new BrowserPrintError(
        'no_printers',
        'Browser Print is running but found no printers. Check that the Zebra is powered on and connected by USB.'
      )
    }
    return printers
  },

  async defaultPrinter() {
    const t = await transport()
    return t.defaultPrinter()
  },

  // remembered -> Browser Print's own default -> the only one -> null, meaning
  // "ask the user"
  async resolvePrinter() {
    const printers = await this.listPrinters()
    const rememberedUid = this.remembered()
    if (rememberedUid) {
      const match = printers.find((p) => p.uid === rememberedUid)
      if (match) return { printer: match, printers, source: 'remembered' }
      this.forget()
    }

    if (printers.length === 1) return { printer: printers[0], printers, source: 'only' }

    try {
      const fallback = await this.defaultPrinter()
      if (fallback) {
        const match = printers.find((p) => p.uid === fallback.uid) || fallback
        return { printer: match, printers, source: 'default' }
      }
    } catch (e) {
      // no default configured is not an error — fall through and ask
    }

    return { printer: null, printers, source: 'ambiguous' }
  },

  async send(printer, zpl) {
    if (!printer) throw new BrowserPrintError('printer_not_found', 'No printer was selected.')
    const t = await transport()
    const data = Array.isArray(zpl) ? zpl.join('') : zpl
    await t.send(printer, data)
  },

  remembered() {
    try {
      return window.localStorage.getItem(STORAGE_KEY)
    } catch (e) {
      return null
    }
  },

  remember(uid) {
    try {
      window.localStorage.setItem(STORAGE_KEY, uid)
    } catch (e) {
      // private browsing / storage disabled — the picker just reappears next time
    }
  },

  forget() {
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch (e) {
      // nothing to do
    }
  }
}

window.ZebraBrowserPrint = ZebraBrowserPrint
export default ZebraBrowserPrint
